import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  RefreshCw,
  Sparkles,
  ShieldCheck,
} from "lucide-react";
import { format } from "date-fns";
import { useAuth } from "@/lib/auth";
import { useOrganization } from "@/lib/organization-context";
import { useAdminSupport } from "@/hooks/useAdminSupport";
import { successToast, errorToast, infoToast } from "@/components/ui/sonner";
import {
  AdminPageHeader,
  AdminPageSkeleton,
  AdminHeroMetric,
  AdminHeroAsideStat,
  AdminStatCard,
  AdminSection,
  AdminPanel,
  AdminTable,
  AdminPill,
  adminTableHeadCellClass,
  adminTableRowClass,
  adminTableCellClass,
} from "@/components/admin/AdminUI";

interface Metrics {
  generatedAt: string;
  weekly: {
    rangeStart: string;
    rangeEnd: string;
    mrrCents: number;
    mrrCurrency: string;
    mrrDeltaCents: number;
  };
  monthly: {
    rangeStart: string;
    rangeEnd: string;
    newSignups: number;
    activatedSignups: number;
    activationRate: number;
    proUpgradedToTeam: number;
    proTotal: number;
    upgradeRate: number;
  };
}

const fmtMoney = (cents: number, currency: string) => {
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const { isSuperAdmin } = useAdminSupport();
  const [reseeding, setReseeding] = useState(false);
  const [auditing, setAuditing] = useState(false);
  const [auditResult, setAuditResult] = useState<{
    generatedAt: string;
    mode: string;
    totalRows: number;
    uniqueCustomers: number;
    flaggedCount: number;
    flaggedRows: Array<{
      table: string;
      record_id: string;
      customer_id: string;
      organization_id?: string | null;
      created_at?: string | null;
      reason: string;
      stripe_error?: string;
    }>;
  } | null>(null);

  const handleAuditStripeIds = async () => {
    setAuditing(true);
    setAuditResult(null);
    infoToast("Running Stripe ID audit…", "Verifying every stored customer ID against live Stripe.");
    try {
      const { data, error } = await supabase.functions.invoke("audit-stripe-ids", { body: {} });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      setAuditResult(data as any);
      const flagged = (data as any).flaggedCount ?? 0;
      if (flagged === 0) {
        successToast("All clear", `${(data as any).uniqueCustomers} customer IDs verified against live Stripe.`);
      } else {
        errorToast(`${flagged} flagged row(s)`, "Test-mode or missing customers detected — review below.");
      }
    } catch (e: any) {
      errorToast("Audit failed", e?.message ?? String(e));
    } finally {
      setAuditing(false);
    }
  };

  const handleReseed = async () => {
    if (!user?.id || !organization?.id) {
      errorToast("Missing context", "User or organization not loaded yet.");
      return;
    }
    const ok = window.confirm(
      `Reseed sample content into "${organization.name}"?\n\nThis wipes existing [SAMPLE] content (proposal + tagged vault items) and inserts a fresh copy.`,
    );
    if (!ok) return;
    setReseeding(true);
    infoToast("Reseeding…", "Wiping and re-inserting sample content.");
    try {
      const { data, error } = await supabase.functions.invoke("seed-sample-content", {
        body: { user_id: user.id, organization_id: organization.id, force: true },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      successToast("Sample content reseeded", `Vault items: ${(data as any)?.vault_items ?? "?"}`);
    } catch (e: any) {
      errorToast("Reseed failed", e?.message ?? String(e));
    } finally {
      setReseeding(false);
    }
  };

  const { data, isLoading, isFetching, refetch, error } = useQuery({
    queryKey: ["admin-metrics"],
    queryFn: async (): Promise<Metrics> => {
      const { data, error } = await supabase.functions.invoke("admin-metrics", { body: {} });
      if (error) throw error;
      return data as Metrics;
    },
  });

  if (isLoading) return <AdminPageSkeleton stats={3} rows={4} columns={4} />;

  if (error || !data) {
    return (
      <div
        className="rounded-[4px] px-6 py-5 text-[13px] text-destructive"
        style={{ background: "#faf7f1", border: "1px solid rgba(220,38,38,0.18)" }}
      >
        Failed to load metrics: {(error as Error)?.message ?? "unknown error"}
      </div>
    );
  }

  const w = data.weekly;
  const m = data.monthly;
  const mrrDeltaNegative = w.mrrDeltaCents < 0;
  const mrrDeltaText = mrrDeltaNegative
    ? `−${fmtMoney(-w.mrrDeltaCents, w.mrrCurrency)}`
    : `+${fmtMoney(w.mrrDeltaCents, w.mrrCurrency)}`;
  const mrrDelta = mrrDeltaNegative
    ? <span style={{ color: "#b91c1c" }}>{mrrDeltaText}</span>
    : mrrDeltaText;

  return (
    <div className="space-y-10">
      <AdminPageHeader
        eyebrow="Atelier overview"
        title="Dashboard"
        description="A weekly read on revenue, activation, and conversion across the platform."
        meta={`Generated ${format(new Date(data.generatedAt), "MMM d, yyyy 'at' HH:mm 'UTC'")}`}
        actions={
          <>
            {isSuperAdmin && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReseed}
                disabled={reseeding}
                className="text-[11px] uppercase tracking-[0.18em] h-8"
              >
                {reseeding ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-2" />}
                Reseed
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
              className="text-[11px] uppercase tracking-[0.18em] h-8"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-2 ${isFetching ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </>
        }
      />

      <AdminHeroMetric
        eyebrow={`MRR · ${format(new Date(w.rangeStart), "MMM d")} – ${format(new Date(w.rangeEnd), "MMM d")}`}
        value={fmtMoney(w.mrrCents, w.mrrCurrency)}
        caption={`${mrrDeltaText} ${mrrDeltaNegative ? "lost" : "added"} this week. A snapshot of every active subscription right now, normalised to monthly.`}
        aside={
          <>
            <AdminHeroAsideStat label="Weekly Δ" value={mrrDelta} />
            <AdminHeroAsideStat label="Currency" value={w.mrrCurrency.toUpperCase()} />
          </>
        }
      />

      <AdminSection
        title={`90-day cohort · ${format(new Date(m.rangeStart), "MMM d")} – ${format(new Date(m.rangeEnd), "MMM d")}`}
        description="Acquisition, activation, and plan-tier conversion."
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <AdminStatCard
            label="New signups"
            value={m.newSignups}
            sub={`${m.activatedSignups} exported a proposal within 14d`}
          />
          <AdminStatCard
            label="Activation rate"
            value={`${(m.activationRate * 100).toFixed(1)}%`}
            sub="Signup → first export within 14d"
            tone="success"
          />
          <AdminStatCard
            label="Team activations MTD"
            value={m.proUpgradedToTeam}
            sub="Active Team plan subscriptions this month"
            tone="info"
          />
        </div>
      </AdminSection>

      <AdminSection
        title="Pre-launch checks"
        description="Run targeted health checks against live billing."
      >
        <AdminPanel padded>
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="min-w-0 max-w-2xl">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="w-3.5 h-3.5" style={{ color: "#c2541a" }} />
                <h3
                  className="text-[18px] text-foreground"
                  style={{
                    fontFamily: "Outfit, sans-serif",
                    fontWeight: 300,
                    letterSpacing: "-0.01em",
                  }}
                >
                  Stripe ID audit
                </h3>
              </div>
              <p className="text-[12.5px] text-muted-foreground leading-relaxed">
                Verifies every <code className="font-mono text-[11px]">stripe_customer_id</code>{" "}
                in <code className="font-mono text-[11px]">subscriptions</code> and{" "}
                <code className="font-mono text-[11px]">dunning_attempts</code> against the live Stripe key.
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleAuditStripeIds}
              disabled={auditing}
              className="text-[11px] uppercase tracking-[0.18em] h-8"
            >
              {auditing ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5 mr-2" />}
              Run audit
            </Button>
          </div>

          {auditResult && (
            <div className="mt-6 space-y-4">
              <div className="flex flex-wrap gap-x-8 gap-y-2 text-[11.5px] text-muted-foreground tabular-nums">
                <span>
                  Mode <span className="text-foreground font-mono ml-1">{auditResult.mode}</span>
                </span>
                <span>
                  Rows <span className="text-foreground ml-1">{auditResult.totalRows}</span>
                </span>
                <span>
                  Unique <span className="text-foreground ml-1">{auditResult.uniqueCustomers}</span>
                </span>
                <span className="flex items-center gap-1.5">
                  Flagged
                  <AdminPill tone={auditResult.flaggedCount === 0 ? "success" : "danger"}>
                    {auditResult.flaggedCount}
                  </AdminPill>
                </span>
              </div>

              {auditResult.flaggedCount === 0 ? (
                <div className="text-[12.5px] text-[#16a34a]">
                  All Stripe customer IDs verified against live mode.
                </div>
              ) : (
                <AdminTable
                  head={
                    <>
                      <th className={adminTableHeadCellClass}>Table</th>
                      <th className={adminTableHeadCellClass}>Record</th>
                      <th className={adminTableHeadCellClass}>Customer</th>
                      <th className={adminTableHeadCellClass}>Reason</th>
                    </>
                  }
                >
                  {auditResult.flaggedRows.map((r, i, arr) => (
                    <tr
                      key={i}
                      className={adminTableRowClass}
                      style={{ borderBottom: i === arr.length - 1 ? "none" : "1px solid rgba(58,53,48,0.06)" }}
                    >
                      <td className={`${adminTableCellClass} font-mono text-[11.5px] text-muted-foreground`}>{r.table}</td>
                      <td className={`${adminTableCellClass} font-mono text-[11.5px] text-muted-foreground`}>{r.record_id}</td>
                      <td className={`${adminTableCellClass} font-mono text-[11.5px] text-muted-foreground`}>{r.customer_id}</td>
                      <td className={adminTableCellClass}>
                        <AdminPill tone="danger">{r.reason}</AdminPill>
                        {r.stripe_error && (
                          <div className="text-[11px] text-muted-foreground mt-1.5 break-all">{r.stripe_error}</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </AdminTable>
              )}
            </div>
          )}
        </AdminPanel>
      </AdminSection>
    </div>
  );
}
